import React, { useCallback, useMemo, useState } from 'react';
import type { AddAllergyRequest, UpdateAllergyRequest } from '@features/patients';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, Chip, IconButton, useTheme } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridDensity,
  type GridRowId,
  type GridRowModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';

import type { Allergy } from '../types';

type AllergySeverity = 'mild' | 'moderate' | 'severe' | 'life-threatening';

interface PatientAllergiesEditCardProps {
  allergies: Allergy[];
  onAddAllergy: (request: AddAllergyRequest) => Promise<Allergy>;
  onUpdateAllergy: (allergyId: string, patch: UpdateAllergyRequest) => Promise<void>;
  onDeleteAllergy: (allergyId: string) => Promise<void>;
  isLoading?: boolean;
  patientId?: string;
  /** Source identifier for analytics */
  source?: string;
}

interface CustomToolbarProps {
  onAddRow: () => void;
  isLoading: boolean;
  buttonLabel: string;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({ onAddRow, isLoading, buttonLabel }) => {
  return (
    <GridToolbarContainer
      sx={{
        mb: 3,
        gap: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& .MuiButton-root': {
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 500,
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </Box>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={onAddRow}
        disabled={isLoading}
        sx={{
          borderRadius: '999px',
          textTransform: 'none',
          fontWeight: 500,
          px: 3,
          bgcolor: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          '&:disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled',
          },
        }}
      >
        {buttonLabel}
      </Button>
    </GridToolbarContainer>
  );
};

const formatDate = (date: string | null | undefined): string => {
  if (!date) return '';
  return date.split('T')[0];
};

const toIsoDate = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date) return value.toISOString().split('T')[0];
  return null;
};

// Fields that should always be visible regardless of data
const ALWAYS_VISIBLE_ALLERGY_FIELDS = ['allergen', 'is_active', 'actions'];

export const PatientAllergiesEditCard: React.FC<PatientAllergiesEditCardProps> = ({
  allergies,
  onAddAllergy,
  onUpdateAllergy,
  onDeleteAllergy,
  isLoading = false,
  patientId,
  source = 'context_bar',
}) => {
  const theme = useTheme();
  const [density, setDensity] = useState<GridDensity>('compact');
  const apiRef = useGridApiRef();
  const { patient: patientTracking } = useEventTracking();

  const initialColumnVisibilityModel = useMemo(() => {
    const visibilityModel: GridColumnVisibilityModel = {};
    const fields = ['allergen', 'reaction', 'severity', 'onset_date', 'is_active', 'actions'];

    fields.forEach(field => {
      if (ALWAYS_VISIBLE_ALLERGY_FIELDS.includes(field)) {
        visibilityModel[field] = true;
        return;
      }

      if (allergies.length === 0) {
        visibilityModel[field] = true;
        return;
      }

      const hasValue = allergies.some(allergy => {
        const value = allergy[field as keyof Allergy];
        return value !== null && value !== undefined && value !== '';
      });
      visibilityModel[field] = hasValue;
    });

    return visibilityModel;
  }, [allergies]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );

  const handleDeleteClick = useCallback(
    async (id: GridRowId) => {
      const allergyId = id as string;
      await onDeleteAllergy(allergyId);
      if (patientId) {
        patientTracking.updated(patientId, {
          source,
          fields_updated: ['allergies_item_deleted'],
        });
      }
    },
    [onDeleteAllergy, patientId, patientTracking, source],
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      const allergyId = newRow.allergy_id as string;
      const patch: UpdateAllergyRequest = {
        allergen: newRow.allergen ?? null,
        reaction: newRow.reaction ?? null,
        severity: (newRow.severity as AllergySeverity | null) ?? null,
        onset_date: toIsoDate(newRow.onset_date),
        is_active: newRow.is_active ?? null,
      };

      try {
        await onUpdateAllergy(allergyId, patch);
        if (patientId) {
          patientTracking.updated(patientId, {
            source,
            fields_updated: ['allergies_item_edited'],
          });
        }
        return newRow;
      } catch {
        return oldRow;
      }
    },
    [onUpdateAllergy, patientId, patientTracking, source],
  );

  const handleAddRow = useCallback(async () => {
    const defaultName = `New Allergy ${allergies.length + 1}`;
    const created = await onAddAllergy({ allergen: defaultName, is_active: true });

    setTimeout(() => {
      if (apiRef.current && created.allergy_id) {
        apiRef.current.startCellEditMode({
          id: created.allergy_id,
          field: 'allergen',
        });
      }
    }, 100);

    if (patientId) {
      patientTracking.updated(patientId, {
        source: 'context_bar',
        fields_updated: ['allergies_item_added'],
      });
    }
  }, [allergies.length, onAddAllergy, apiRef, patientId, patientTracking]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'allergen',
        headerName: 'Allergy',
        minWidth: 180,
        flex: 1.5,
        editable: true,
      },
      {
        field: 'reaction',
        headerName: 'Reaction',
        minWidth: 160,
        flex: 1.2,
        editable: true,
      },
      {
        field: 'severity',
        headerName: 'Severity',
        minWidth: 120,
        flex: 0.8,
        editable: true,
        type: 'singleSelect',
        valueOptions: ['mild', 'moderate', 'severe', 'life-threatening'],
      },
      {
        field: 'onset_date',
        headerName: 'Onset Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.onset_date;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.onset_date),
      },
      {
        field: 'is_active',
        headerName: 'Status',
        flex: 1,
        editable: true,
        type: 'boolean',
        renderCell: params => {
          const isActive = params.value !== false;
          return (
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                borderRadius: '999px',
                bgcolor: isActive ? 'rgba(103, 80, 164, 0.12)' : 'rgba(121, 116, 126, 0.12)',
                color: isActive ? 'rgba(103, 80, 164, 1)' : 'rgba(121, 116, 126, 1)',
                fontWeight: 500,
                border: 'none',
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        editable: false,
        renderCell: params => (
          <IconButton
            size="small"
            aria-label="Delete allergy"
            onClick={e => {
              e.stopPropagation();
              void handleDeleteClick(params.id);
            }}
            sx={{
              color: 'grey.500',
              '&:hover': {
                color: 'error.main',
              },
            }}
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleDeleteClick],
  );

  const rows = useMemo(() => {
    return allergies.map(allergy => ({ ...allergy, id: allergy.allergy_id }));
  }, [allergies]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          density={density}
          onDensityChange={newDensity => setDensity(newDensity)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          hideFooter
          slots={{
            toolbar: () => (
              <CustomToolbar
                onAddRow={() => void handleAddRow()}
                isLoading={isLoading}
                buttonLabel="Add Allergy"
              />
            ),
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
              borderRadius: '12px',
              fontWeight: 600,
              borderBottom: 'none',
            },
            '& .MuiDataGrid-row': {
              borderRadius: '8px',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
};
